import * as React from 'react';
import { DockviewReact } from 'dockview';
import type {
  DockviewReadyEvent,
  IDockviewPanelProps,
  IDockviewPanelHeaderProps,
  DockviewApi,
  DockviewDidDropEvent,
  Position,
} from 'dockview';
import 'dockview/dist/styles/dockview.css';
import './FormaDock.css';
import { Prefs } from '../services/prefsService';
import { MODULE_DRAG_MIME } from '../../shared/spec-modules';
import { FirmPrefs } from '../services/firmPrefsService';
import { Hint } from './ui/tooltip';
import { REM_BASE_PX } from '../lib/rem';
import { subscribeUiScale } from '../lib/uiScale';
import {
  PANEL_CAP_FRACTION,
  clampPanelPx,
  createDockSuspension,
  enforceCanvasFloor,
  layoutForWorkspace,
  renameLegacyPanels,
  makeDockBlob,
  parseDockBlob,
  type DockBlob,
  type DockLayout,
} from '../lib/dockLayout';

/** Floor every dockable panel is guaranteed. Authored at the 100% interface
 *  scale (220 = 13.75rem, 140 = 8.75rem) and multiplied by the live root
 *  font-size, so the floor follows the dial (DESIGN.md §3.6) — dockview
 *  enforces it via panel constraints. Internal scrolling is the exception for
 *  genuinely long lists — a panel should never be squished below usable. */
const DEFAULT_MIN_PANEL_WIDTH = 220;
const DEFAULT_MIN_PANEL_HEIGHT = 140;
/** The canvas is the hero (DESIGN.md §3.4/§3.6): it is never capped, but it
 *  was never given a FLOOR either — every rail's width was reapplied after
 *  it docked and the canvas silently absorbed "whatever's left", which is
 *  fine for one rail landing once. A palette added later (a shipped-after
 *  migration, or its intended dock-mate having been closed so it lands as
 *  its own rail instead of stacking) never rebalanced anything, so the
 *  leftover kept shrinking with every addition. Below this fraction of the
 *  dock body, the newest/largest rails give width back until the canvas
 *  clears it or every rail is at its own floor. */
const CANVAS_MIN_FRACTION = 0.6;
/** Height of a top/bottom strip when a palette is docked there. */
const STRIP_HEIGHT = 150;

/** The live root font-size (16px × the interface dial × the user's factor). */
function rootPx(): number {
  try {
    return parseFloat(getComputedStyle(document.documentElement).fontSize) || REM_BASE_PX;
  } catch {
    return REM_BASE_PX;
  }
}

/** A size authored at 100% → the same size at the current dial. `scalePx(320)`
 *  is exactly 320 at 100%, 384 at 120%. */
function scalePx(px: number): number {
  return Math.round((px / REM_BASE_PX) * rootPx());
}

/* ─── Layout persistence ───
   Stored as { v, ts, w, h, norm, layout, seen } in BOTH localStorage (fast
   first paint) and user_prefs (follows the user across machines). Newest ts
   wins. Every panel size inside `layout` is a FRACTION of the workspace along
   its own axis (DESIGN.md §3.6), so a 320px rail saved on a 2560px monitor
   restores as the same share of a 1440px laptop, never as 320 fixed pixels
   eating the screen. The shape, the one-time migration off the old pixel
   records, and the floor/cap maths all live in ../lib/dockLayout (pure +
   unit-tested); this file owns measuring, writing and the live panels.

   `seen` = every palette id this layout has ever been offered. A palette
   missing from `seen` is NEW (shipped after the layout was saved) and gets
   added on restore; one that's in `seen` but not in the layout was
   deliberately closed and stays closed. This is why adding a palette never
   needs a layoutKey bump — bumping wipes everyone's arrangement. */

/** Automated capture/QA runs sign in as a real user; without this they would
    overwrite that user's CLOUD layout with a throwaway one and their next
    launch would "revert" to the harness layout. Local persistence still works
    so a harness can test restore within its own profile. */
const cloudPersistDisabled = () => {
  try {
    return localStorage.getItem('sf.dockNoCloudPersist') === '1';
  } catch {
    return false;
  }
};

/** Read a stored record (a localStorage string or the object Prefs hands
 *  back) into the current shape. `migrated: true` means it was an older
 *  pixel-shaped record and the caller should write the converted blob back.
 *  A record we cannot read returns null — the caller shows the default
 *  layout and LEAVES THE RECORD ALONE; a layout is never thrown away. */
function readBlob(value: unknown, cur: { w: number; h: number }) {
  return parseDockBlob(value, cur);
}

/**
 * FormaDock — the Bluebeam-style dockable workspace for the Forma designer
 * (and, later, the spec editors). Built on the dockview docking engine.
 *
 * Interaction model (Josh's spec):
 *  • LOCKED = clean work view. No close buttons, no drag, no resize. Nothing to
 *    accidentally break. This is the default.
 *  • UNLOCKED = edit the layout. A tray of every available tool palette appears at
 *    the top; drag one down onto the dock (or click it) to place it. Panels show a
 *    close (×); closing one returns it to the tray — nothing is ever lost.
 *  • The Document (canvas) can never be closed.
 *
 * Content isn't rewritten: each panel is a portal host that hands its DOM container
 * back to the parent, which React-portals the existing panel JSX into it. Every
 * current function is preserved. See memory [[project-dockable-workspace]].
 */

export interface PaletteDef {
  id: string;
  title: string;
  /** false → no close button, ever (the Document). Defaults to true. */
  closable?: boolean;
  /** true → hide the tab header entirely (the Document is the anchored core). */
  noTab?: boolean;
  /** Where it lands in the default layout / when re-added from the tray. */
  defaultPosition?: {
    direction: 'left' | 'right' | 'above' | 'below' | 'within';
    /** id of the panel to position relative to (defaults to the canvas). */
    reference?: string;
  };
  /** Preferred width in px when docked left/right. */
  width?: number;
  /** true → the palette exists and is fully usable, but is NOT part of the
      default layout (the user opens it from the menu or the tray when they
      want it). Additive: omitted, behaviour is exactly as before. */
  defaultClosed?: boolean;
  /** Minimum usable width/height in px. Defaults to a sane floor
   *  (DEFAULT_MIN_PANEL_*) when omitted. */
  minWidth?: number;
  minHeight?: number;
}

interface DockContextValue {
  locked: boolean;
  onMount: (id: string, el: HTMLElement) => void;
  onUnmount: (id: string) => void;
  closePanel: (id: string) => void;
  isClosable: (id: string) => boolean;
  /** The glyph registered for this panel (workspace-supplied `panelIcons`),
   *  or undefined if none was registered. */
  iconOf: (id: string) => React.ReactNode | undefined;
  /** How many panels share this panel's dockview group right now — 2+ means
   *  its zone renders the compact icon rail instead of text tabs. */
  siblingCount: (id: string) => number;
}
const DockContext = React.createContext<DockContextValue | null>(null);

/** Portal host: exposes its content DOM node upward via context. */
function PanelHost(props: IDockviewPanelProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const ctx = React.useContext(DockContext);
  const id = props.api.id;
  React.useEffect(() => {
    if (ref.current && ctx) ctx.onMount(id, ref.current);
    return () => ctx?.onUnmount(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // When a palette is docked top/bottom it's wide + short — flip its contents to a
  // horizontal flow so it takes less vertical room. Detected from the box shape.
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const { width, height } = el.getBoundingClientRect();
      el.classList.toggle('forma-panel-horizontal', width >= 520 && width > height * 1.5);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      data-dock-panel={id}
      style={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    />
  );
}

/** Custom tab: title + a close (×) shown only when unlocked and the panel is
 *  closable. A zone holding 2+ toolboxes is clutter as plain text tabs — once
 *  a panel has a registered icon AND shares its group with another panel,
 *  the tab collapses to that icon alone (tooltip = the panel name), giving a
 *  slim icon rail. A single-panel zone, or a panel with no icon, keeps the
 *  unchanged text tab. */
function FormaTab(props: IDockviewPanelHeaderProps) {
  const ctx = React.useContext(DockContext);
  const id = props.api.id;
  const [title, setTitle] = React.useState(props.api.title ?? id);
  React.useEffect(() => {
    const d = props.api.onDidTitleChange(() => setTitle(props.api.title ?? id));
    return () => d.dispose();
  }, [props.api, id]);
  const showClose = !!ctx && !ctx.locked && ctx.isClosable(id);
  const icon = ctx?.iconOf(id);
  const compact = !!icon && (ctx?.siblingCount(id) ?? 1) >= 2;
  return (
    <div className={`forma-tab${compact ? ' forma-tab-compact' : ''}`}>
      {compact ? (
        <Hint text={title} side="bottom">
          <span className="forma-tab-icon" role="img" aria-label={title}>
            {icon}
          </span>
        </Hint>
      ) : (
        <span className="forma-tab-title">{title}</span>
      )}
      {showClose && (
        <button
          className="forma-tab-close"
          title="Close — drag its top-bar button back in to reopen it"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            ctx.closePanel(id);
          }}
        >
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 4l8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

const COMPONENTS = { panel: PanelHost };
const TAB_COMPONENTS = { forma: FormaTab };

const POSITION_TO_DIRECTION: Record<string, 'left' | 'right' | 'above' | 'below' | 'within'> = {
  left: 'left',
  right: 'right',
  top: 'above',
  bottom: 'below',
  center: 'within',
};

export interface FormaDockHandle {
  showPanel: (id: string) => void;
  hidePanel: (id: string) => void;
  togglePanel: (id: string) => void;
  isPanelOpen: (id: string) => boolean;
  /** Back to square one: drops the user's saved layout so the ladder
   *  (firm default → out-of-box) resolves again. */
  resetLayout: () => void;
  /** The arrangement on screen right now, in the stored blob shape —
   *  what "Set as firm default" publishes (contract §2.9). */
  snapshotLayout: () => unknown;
  /** Review mode: hold the live layout aside, hide every panel except the
   *  Document, and block all saves. Safe to call twice. */
  suspend: () => void;
  /** Put the held layout back exactly as it was; never saves. No-op when
   *  nothing is suspended. */
  resume: () => void;
}

export interface FormaDockProps {
  isDark: boolean;
  locked: boolean;
  layoutKey: string;
  /** Every palette that can appear in this workspace (first entry = the Document). */
  palettes: PaletteDef[];
  /** firm_prefs key holding this workspace's FIRM default layout. The
   *  resolution ladder is user → firm → out-of-box (contract §2.9): a user
   *  who has saved their own arrangement never sees the firm's. */
  firmLayoutKey?: string;
  /** Panel id → glyph, for the tab (full text mode) and the icon rail
   *  (compact mode, 2+ panels sharing a zone). A panel with no entry here
   *  never goes compact — it keeps its plain text tab. */
  panelIcons?: Record<string, React.ReactNode>;
  onContainersChange: (containers: Record<string, HTMLElement>) => void;
  onReady?: (handle: FormaDockHandle) => void;
}

export function FormaDock({
  isDark,
  locked,
  layoutKey,
  palettes,
  firmLayoutKey,
  panelIcons,
  onContainersChange,
  onReady,
}: FormaDockProps) {
  const apiRef = React.useRef<DockviewApi | null>(null);
  const elsRef = React.useRef<Record<string, HTMLElement>>({});
  const palettesRef = React.useRef(palettes);
  palettesRef.current = palettes;
  const panelIconsRef = React.useRef(panelIcons);
  panelIconsRef.current = panelIcons;
  const [openIds, setOpenIds] = React.useState<string[]>([]);
  // Icon rail (Cohesion R1 Phase 5): how many panels share each panel's own
  // dockview group right now. Computed centrally (not per-tab) so it stays
  // correct through every drag/split/close dockview fires.
  const [groupSizes, setGroupSizes] = React.useState<Record<string, number>>({});
  const computeGroupSizes = React.useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const sizes: Record<string, number> = {};
    for (const g of api.groups) {
      const n = g.panels.length;
      for (const p of g.panels) sizes[p.id] = n;
    }
    setGroupSizes(sizes);
  }, []);
  // Always-current key: the old code captured layoutKey in a closure at
  // dock-ready, so when the key changed (anon → signed-in user id) every save
  // landed under the WRONG key and the next launch "reset" the workspace.
  const layoutKeyRef = React.useRef(layoutKey);
  layoutKeyRef.current = layoutKey;
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const firmKeyRef = React.useRef(firmLayoutKey);
  firmKeyRef.current = firmLayoutKey;
  // ts of the layout currently on screen — cloud copies older than this never
  // clobber what the user is looking at.
  const appliedTsRef = React.useRef(0);
  // Latest tryCloud closure — re-run on window focus so a layout tweaked on
  // the other machine lands as soon as you come back to this one.
  const tryCloudRef = React.useRef<(() => void) | null>(null);
  // Palette ids this layout has already been offered (see DockBlob.seen).
  const seenRef = React.useRef<string[]>([]);
  // Late-bound so the default builder (declared first) can take the floor/cap
  // baseline the moment it finishes laying panels out.
  const applyClampsRef = React.useRef<(() => void) | null>(null);
  // Late-bound the same way, for the same reason: addPalette (declared
  // first) needs to call it every time it lands a panel, not just on resize.
  const protectCanvasRef = React.useRef<(() => void) | null>(null);
  // Blocks protectCanvas from re-entering on a layout change IT JUST
  // DISPATCHED (onDidLayoutChange fires from its own fromJSON call too) —
  // belt-and-suspenders alongside the byte-identical check below, which is
  // what actually stops an unsatisfiable floor from looping forever.
  const protectSuppressUntilRef = React.useRef(0);
  // The serialized grid protectCanvas last corrected FROM. Seeing it again
  // means the correction was refused — see the check inside protectCanvas.
  const protectRefusedRef = React.useRef<string | null>(null);
  // Workspace + root font-size the layout on screen was laid out for. The
  // floor/cap only re-sizes panels when one of these CHANGES — see applyClamps.
  const clampBaseRef = React.useRef<{ w: number; h: number; root: number } | null>(null);

  const defOf = (id: string) => palettesRef.current.find((p) => p.id === id);

  const emitContainers = React.useCallback(() => {
    onContainersChange({ ...elsRef.current });
  }, [onContainersChange]);

  const refreshOpen = React.useCallback(() => {
    const api = apiRef.current;
    if (api) setOpenIds(api.panels.map((p) => p.id));
  }, []);

  const isClosable = React.useCallback((id: string) => defOf(id)?.closable !== false, []);

  const ctxValue = React.useMemo<DockContextValue>(
    () => ({
      locked,
      onMount: (id, el) => {
        elsRef.current[id] = el;
        emitContainers();
      },
      onUnmount: (id) => {
        delete elsRef.current[id];
        emitContainers();
      },
      closePanel: (id) => apiRef.current?.getPanel(id)?.api.close(),
      isClosable,
      iconOf: (id) => panelIconsRef.current?.[id],
      siblingCount: (id) => groupSizes[id] ?? 1,
    }),
    [locked, emitContainers, isClosable, groupSizes]
  );

  const addPalette = React.useCallback((id: string) => {
    const api = apiRef.current;
    const def = defOf(id);
    if (!api || !def || api.getPanel(id)) return;
    const pos = def.defaultPosition;
    // A reference panel the user has closed would make addPanel throw and the
    // palette would silently never appear — fall back to the canvas anchor.
    const ref = pos?.reference ?? 'canvas';
    const refOk = api.getPanel(ref) ? ref : 'canvas';
    const position = pos
      ? { referencePanel: refOk, direction: refOk === ref ? pos.direction : 'right' }
      : undefined;
    api.addPanel({
      id,
      component: 'panel',
      tabComponent: 'forma',
      title: def.title,
      position: position as any,
      minimumWidth: scalePx(def.minWidth ?? DEFAULT_MIN_PANEL_WIDTH),
      minimumHeight: scalePx(def.minHeight ?? DEFAULT_MIN_PANEL_HEIGHT),
    });
    if (def.width) {
      try {
        api.getPanel(id)?.api.setSize({ width: scalePx(def.width) });
      } catch {
        /* best-effort */
      }
    }
    protectCanvasRef.current?.();
  }, []);

  // Panels flagged noTab (the Document) are the anchored core: hide their tab
  // header and refuse drops into their group, so they always read as the canvas
  // the palettes dock around — never a closable/tabbed panel.
  const enforceAnchors = React.useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    for (const def of palettesRef.current) {
      if (!def.noTab) continue;
      const panel = api.getPanel(def.id);
      if (panel?.group) {
        try {
          panel.group.locked = 'no-drop-target';
          panel.group.header.hidden = true;
        } catch {
          /* best-effort */
        }
      }
    }
  }, []);

  const buildDefault = React.useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    try {
      api.clear();
    } catch {
      /* nothing to clear */
    }
    for (const def of palettesRef.current) {
      if (def.defaultClosed) continue;
      addPalette(def.id);
    }
    // A defaultClosed palette has still been OFFERED — record it as seen so
    // the additive-migration path below never force-opens it later.
    seenRef.current = palettesRef.current.map((p) => p.id);
    // dockview activates each panel as it's added, so every tabbed group
    // would open on its LAST tab (the fresh-default "why is Watermark
    // showing?" bug). Activate each group's FIRST tab instead.
    try {
      for (const group of api.groups) {
        group.panels[0]?.api.setActive();
      }
    } catch {
      /* best-effort */
    }
    // Re-apply the side-dock widths LAST. Each addPanel splits its reference
    // 50/50 before the per-panel setSize lands, so an early width gets undone
    // by whatever docks next — and the canvas ends up squeezed between two
    // half-screen rails. The canvas is the hero (DESIGN §3.4): it keeps every
    // pixel the palettes did not ask for.
    try {
      for (const def of palettesRef.current) {
        const dir = def.defaultPosition?.direction;
        if (!def.width || (dir !== 'left' && dir !== 'right')) continue;
        api.getPanel(def.id)?.api.setSize({ width: scalePx(def.width) });
      }
    } catch {
      /* best-effort */
    }
    api.getPanel('canvas')?.api.setActive();
    enforceAnchors();
    applyClampsRef.current?.();
  }, [addPalette, enforceAnchors]);

  const dockSize = React.useCallback((): { w: number; h: number } => {
    const r = bodyRef.current?.getBoundingClientRect();
    return { w: Math.round(r?.width || 0), h: Math.round(r?.height || 0) };
  }, []);

  /** Give the canvas its floor back. DESIGN.md §3.6: the canvas is the hero
   *  and always keeps a majority of the dock's WIDTH — not "whatever the
   *  rails left over", which is what every prior fix actually shipped
   *  (postmortem: T1.2, and every dock-squeeze report before it). This is
   *  the one path that runs on every trigger that can create or resize a
   *  zone — fresh default, restore (user's own AND the firm's), a palette
   *  shipped after a saved layout migrating in, a live drag to an empty
   *  edge, a window resize, a palette opened/closed from the tray.
   *
   *  Rewrites the SERIALIZED grid (`enforceCanvasFloor`, dockLayout.ts) and
   *  reapplies it via `fromJSON` in one shot, rather than iterative
   *  `panel.api.setSize` calls on the live grid — dockview's own reflow
   *  redistributes freed space to whichever sibling IT considers flexible
   *  next, which was never reliably the canvas, and a second corrective
   *  pass could push a rail straight through its own floor. `fromJSON` is
   *  exactly the mechanism the restore path already trusts for this. */
  const protectCanvas = React.useCallback(() => {
    if (Date.now() < protectSuppressUntilRef.current) return; // our own last fromJSON is still settling
    const api = apiRef.current;
    if (!api) return;
    const canvas = api.getPanel('canvas');
    if (!canvas) return;
    const { w: W, h: H } = dockSize();
    if (!W || !H) return;
    const floor = Math.max(CANVAS_MIN_FRACTION * W, scalePx(DEFAULT_MIN_PANEL_WIDTH));
    const capPx = PANEL_CAP_FRACTION * W;
    const cw = canvas.api.width;
    const canvasShort = !cw || cw < floor - 1;
    // A rail past its cap needs correcting even when the canvas is already
    // healthy — a removal that ballooned a SURVIVING sibling is exactly
    // that case (Josh 2026-09-24), and the canvas is short by definition
    // once it's found (it's the only panel this pass never caps).
    const someRailOverCap = api.panels.some(
      (p) => p.id !== 'canvas' && (p.api.width ?? 0) > capPx + 1
    );
    if (!canvasShort && !someRailOverCap) return; // cheap bail — no JSON round-trip when healthy
    const root = rootPx();
    const floorPxOf = (id: string, axis: 'w' | 'h') => {
      if (id === 'canvas') return 0;
      const def = defOf(id);
      const px =
        axis === 'w'
          ? (def?.minWidth ?? DEFAULT_MIN_PANEL_WIDTH)
          : (def?.minHeight ?? DEFAULT_MIN_PANEL_HEIGHT);
      // dockview enforces the GROUP's own minimum, which was captured in px
      // when the panel was added (and is carried through every toJSON /
      // fromJSON), so it can sit above the floor at today's dial. Asking for
      // less than that is a correction the grid silently refuses — and a
      // refused correction rebuilt the whole dock (canvas included) every
      // ~250ms forever. Plan with the larger of the two.
      const group = api.getPanel(id)?.group;
      const enforced = axis === 'w' ? group?.minimumWidth : group?.minimumHeight;
      const live = typeof enforced === 'number' && Number.isFinite(enforced) ? enforced : 0;
      return Math.max((px / REM_BASE_PX) * root, live);
    };
    try {
      const raw = api.toJSON() as unknown as DockLayout;
      const rawJson = JSON.stringify(raw);
      // The grid came back exactly as it was before our last correction —
      // dockview did not take it, so applying it again changes nothing but
      // remounts every panel. Wait for a real layout change instead.
      if (rawJson === protectRefusedRef.current) return;
      const fixed = enforceCanvasFloor(
        raw,
        W,
        H,
        CANVAS_MIN_FRACTION,
        PANEL_CAP_FRACTION,
        floorPxOf
      );
      // An unsatisfiable floor (not enough room even at everyone's own
      // floor) converges to the SAME output every pass — comparing the
      // serialized result catches that convergence and skips the apply,
      // instead of rebuilding the grid every 250ms forever for no pixel
      // change (the fresh verifier's postmortem: canvasShort never clears).
      if (JSON.stringify(fixed) === rawJson) return;
      // Short — just long enough to eat the one onDidLayoutChange our own
      // fromJSON dispatches, never long enough to swallow a REAL change
      // that lands right after (protectCanvasSoon pushes its wait out past
      // this instead of dropping the request, so nothing is lost either way).
      protectSuppressUntilRef.current = Date.now() + 150;
      protectRefusedRef.current = rawJson;
      api.fromJSON(fixed as unknown as Parameters<typeof api.fromJSON>[0]);
    } catch {
      /* best-effort — a malformed grid leaves the layout exactly as it was */
    }
  }, [dockSize]);
  protectCanvasRef.current = protectCanvas;

  /** Hold every open panel between its dial-aware floor and the fraction cap
   *  (DESIGN.md §3.6) when the WORKSPACE OR THE DIAL CHANGES — never on the
   *  way in. A saved arrangement is the user's own decision: restoring it must
   *  reproduce exactly the pixels it described (a rail they had narrowed below
   *  the floor stays narrowed), so the first call after a restore or a default
   *  build only records the baseline. From then on, a resized window or a
   *  moved dial re-checks that every panel still lands between its floor
   *  (minimum usable size in rem × the live root font-size) and the cap.
   *
   *  The cap only applies along the axis the panel actually docks on: a
   *  full-height rail is capped in WIDTH, a full-width strip in HEIGHT, and a
   *  panel stacked inside a column (full neither way) gets the floor only —
   *  capping it there would fight its neighbours for space the grid has
   *  already shared out. The canvas is the hero and is never touched. */
  const applyClamps = React.useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const { w: W, h: H } = dockSize();
    if (!W || !H) return;
    const root = rootPx();
    // Runs every pass, first call included — the canvas floor isn't a
    // "workspace changed" rule the way the per-panel one below is.
    protectCanvas();
    const base = clampBaseRef.current;
    clampBaseRef.current = { w: W, h: H, root };
    // First application (restore / default build), or nothing actually moved.
    if (!base) return;
    if (Math.abs(base.w - W) <= 1 && Math.abs(base.h - H) <= 1 && base.root === root) return;
    for (const panel of api.panels) {
      if (panel.id === 'canvas') continue;
      const def = defOf(panel.id);
      const floorW = ((def?.minWidth ?? DEFAULT_MIN_PANEL_WIDTH) / REM_BASE_PX) * root;
      const floorH = ((def?.minHeight ?? DEFAULT_MIN_PANEL_HEIGHT) / REM_BASE_PX) * root;
      const w = panel.api.width;
      const h = panel.api.height;
      if (!w || !h) continue;
      const fullW = w >= W - 4;
      const fullH = h >= H - 4;
      if (!fullW) {
        const target = clampPanelPx(w, floorW, fullH ? PANEL_CAP_FRACTION * W : W);
        if (Math.abs(target - w) > 1) {
          try {
            panel.api.setSize({ width: target });
          } catch {
            /* best-effort */
          }
        }
      }
      if (!fullH) {
        const target = clampPanelPx(h, floorH, fullW ? PANEL_CAP_FRACTION * H : H);
        if (Math.abs(target - h) > 1) {
          try {
            panel.api.setSize({ height: target });
          } catch {
            /* best-effort */
          }
        }
      }
    }
  }, [dockSize, protectCanvas]);
  applyClampsRef.current = applyClamps;

  /** Build the blob describing what is on screen right now — panel sizes
   *  normalised to fractions of the workspace they were measured in. */
  const snapshot = React.useCallback((): DockBlob | null => {
    const api = apiRef.current;
    if (!api) return null;
    const { w, h } = dockSize();
    const seen = [...new Set([...seenRef.current, ...api.panels.map((p) => p.id)])];
    return makeDockBlob(api.toJSON(), w, h, seen);
  }, [dockSize]);

  /* Applying an INHERITED layout (the firm default) must not turn the user
     into a customised user — otherwise their first visit would silently
     snapshot the firm's arrangement as their own and they'd never receive an
     updated firm default again. So the write that the restore itself
     triggers is swallowed; the moment they actually drag something, the
     layout becomes theirs and saves normally. */
  const inheritUntilRef = React.useRef(0);
  // Review suspension (T4): while held, nothing may write the layout.
  const suspensionRef = React.useRef(createDockSuspension<unknown>());

  const persistRetryRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // True only while the inherit window was opened by a review resume.
  const retryAfterWindowRef = React.useRef(false);
  const persistRef = React.useRef<(() => void) | null>(null);
  const persist = React.useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    if (suspensionRef.current.isSuspended()) return;
    const waitLeft = inheritUntilRef.current - Date.now();
    if (waitLeft > 0) {
      // Inside the post-review inherit window: retry once just after it ends
      // instead of dropping the change. Only the review resume opts in — a
      // firm-default inherit must never be re-saved as the user's own.
      if (retryAfterWindowRef.current) {
        if (persistRetryRef.current) clearTimeout(persistRetryRef.current);
        persistRetryRef.current = setTimeout(() => persistRef.current?.(), waitLeft + 50);
      }
      return;
    }
    try {
      const { w, h } = dockSize();
      const seen = [...new Set([...seenRef.current, ...api.panels.map((p) => p.id)])];
      seenRef.current = seen;
      const blob = makeDockBlob(api.toJSON(), w, h, seen);
      appliedTsRef.current = blob.ts;
      const key = layoutKeyRef.current;
      localStorage.setItem(key, JSON.stringify(blob));
      // Cloud copy — the layout follows the user to every machine.
      if (!cloudPersistDisabled()) Prefs.set(key, blob);
    } catch {
      /* private mode / quota — non-fatal */
    }
  }, [dockSize]);
  persistRef.current = persist;

  // Debounced save on ANY mouse/drag release inside the dock: sash resizes
  // don't reliably fire onDidLayoutChange, which is exactly why resized
  // panels never stuck.
  const persistSoonRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistSoon = React.useCallback(() => {
    if (persistSoonRef.current) clearTimeout(persistSoonRef.current);
    persistSoonRef.current = setTimeout(persist, 400);
  }, [persist]);

  // Same debounce, for the canvas floor: a LIVE drag that lands a panel on
  // an empty edge (Josh 2026-09-24: "any time a toolbox is docked where
  // there isn't one already") never goes through addPalette — it's an
  // existing panel moving, caught only by onDidLayoutChange / the sash
  // mouseup below. Debounced so it runs once the drag has actually
  // finished, never mid-gesture (a fromJSON mid-drag would tear down the
  // very node being dragged).
  const protectCanvasSoonRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const protectCanvasSoon = React.useCallback(() => {
    if (protectCanvasSoonRef.current) clearTimeout(protectCanvasSoonRef.current);
    // Never schedule INSIDE our own still-active suppress window — that
    // silently dropped a legitimate change that happened to land right
    // after a correction (a live postmortem: closing a panel corrected
    // canvas, and a drag 200ms later landed inside that window and was
    // swallowed for good, nothing ever retried it). Push the wait out
    // past the window instead of dropping the request.
    const wait = Math.max(250, protectSuppressUntilRef.current - Date.now() + 50);
    protectCanvasSoonRef.current = setTimeout(() => protectCanvasRef.current?.(), wait);
  }, []);

  // Window resizes re-flow the dock without any mouseup inside it — keep the
  // saved blob in step so its fractions always describe the layout it contains.
  React.useEffect(() => {
    const onResize = () => persistSoon();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (persistSoonRef.current) clearTimeout(persistSoonRef.current);
    };
  }, [persistSoon]);

  // The workspace itself changing size (window resize, a sibling rail opening,
  // the interface dial moving) re-applies the floor/cap: dockview's grid is
  // proportional, so every panel already keeps its FRACTION of the workspace —
  // what needs re-checking is that the fraction still lands between the
  // dial-aware floor and the cap (DESIGN.md §3.6).
  React.useEffect(() => {
    const el = bodyRef.current;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const soon = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        applyClamps();
        persistSoon();
      }, 150);
    };
    const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(soon) : null;
    ro?.observe(el as Element);
    // The dial changes the root font-size without changing the workspace, so
    // the floor moves on its own — re-clamp on that too.
    const unsubscribe = subscribeUiScale(soon);
    return () => {
      ro?.disconnect();
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [applyClamps, persistSoon]);

  // Cross-machine follow happens on dock mount (editor entry) ONLY. It used
  // to also fire on every window focus — which tore the dock down and
  // re-portaled every palette mid-session, and with any clock skew between
  // machines made the layout "snap back" every alt-tab. Entering the editor
  // is the sync point; while you're in it, your layout is yours.

  const applyBlob = React.useCallback(
    (rawBlob: DockBlob): boolean => {
      const api = apiRef.current;
      if (!api) return false;
      try {
        // Panels renamed by the unified editor restore open in place.
        const blob = renameLegacyPanels(rawBlob, {
          'level-props': { id: 'levels', title: 'Levels' },
          'ds-layout': { id: 'layout', title: 'Layout Options' },
        });
        const { w, h } = dockSize();
        api.fromJSON(
          layoutForWorkspace(blob, w, h) as unknown as Parameters<typeof api.fromJSON>[0]
        );
        applyClamps();
        appliedTsRef.current = blob.ts;
        if (api.panels.length === 0) return false;
        // A palette retired since this layout was saved (e.g. merged into
        // another one, like Project Tokens folding into Tokens 2026-08-26)
        // still restores from the raw JSON — dockview rebuilds an EXISTING
        // panel straight from its serialized id/component, it never consults
        // the app's current palette list. Left alone that's a blank,
        // closable "ghost" tab with no content and a stale title. Drop
        // anything the app no longer defines; dockview collapses the empty
        // group behind it on its own.
        let pruned = false;
        for (const panel of [...api.panels]) {
          if (panel.id !== 'canvas' && !defOf(panel.id)) {
            try {
              panel.api.close();
              pruned = true;
            } catch {
              /* best-effort */
            }
          }
        }
        // Additive migration: a palette shipped AFTER this layout was saved
        // joins it instead of being invisible until someone bumps layoutKey
        // (a bump throws away every user's arrangement — never again).
        // Legacy blobs carry no `seen`, so what's in the layout counts as seen
        // and only genuinely new palettes are added.
        const restoredIds = api.panels.map((p) => p.id);
        seenRef.current = [...new Set([...(blob.seen ?? restoredIds), ...restoredIds])];
        const unseen = palettesRef.current.filter((p) => !seenRef.current.includes(p.id));
        // Everything unseen counts as offered from here on; only the ones that
        // belong in a default layout actually get placed.
        seenRef.current = [...seenRef.current, ...unseen.map((d) => d.id)];
        const missing = unseen.filter((p) => !p.defaultClosed);
        if (missing.length) {
          // dockview activates each panel as it's added, so a palette shipped
          // today would open ON TOP of whatever tab the user left active
          // ("why is the new tool showing?"). Put the active tabs back.
          const wasActive = api.groups.map((g) => g.activePanel?.id).filter(Boolean) as string[];
          for (const def of missing) addPalette(def.id);
          for (const id of wasActive) api.getPanel(id)?.api.setActive();
          api.getPanel('canvas')?.api.setActive();
          console.log(
            '[dock] added new palette(s) to your layout:',
            missing.map((d) => d.id).join(', ')
          );
          persistSoon();
        }
        if (pruned) persistSoon();
        return true;
      } catch (e) {
        console.warn('[dock] layout restore failed — rebuilding default:', e);
        return false;
      }
    },
    [dockSize, addPalette, persistSoon, applyClamps]
  );

  /** Rung 2 of the ladder: the layout an admin published for the firm. */
  const firmBlob = React.useCallback((): DockBlob | null => {
    const fk = firmKeyRef.current;
    if (!fk) return null;
    const raw = FirmPrefs.get(fk);
    if (!raw) return null;
    // Converted in memory only — a firm default is published by an admin, so
    // this user's restore never rewrites it.
    return readBlob(raw, dockSize())?.blob ?? null;
  }, [dockSize]);

  /** Apply an inherited layout without claiming it as the user's own. */
  const applyInherited = React.useCallback(
    (blob: DockBlob): boolean => {
      retryAfterWindowRef.current = false;
      if (persistRetryRef.current) clearTimeout(persistRetryRef.current);
      inheritUntilRef.current = Date.now() + 1500;
      const ok = applyBlob(blob);
      if (!ok) inheritUntilRef.current = 0;
      // An inherited layout is not "what this user last saved", so it must
      // never out-rank the user's own cloud copy on the ts comparison.
      else appliedTsRef.current = 0;
      return ok;
    },
    [applyBlob]
  );

  /** Persist a record we did not just build — the one-time migration of an
   *  older pixel-shaped layout. Same keys, same handlers, same `ts` (so the
   *  cross-machine newest-wins comparison is untouched); only the value shape
   *  changes. The harness/cloud guard applies here like every other write. */
  const writeBack = React.useCallback((key: string, blob: DockBlob) => {
    try {
      localStorage.setItem(key, JSON.stringify(blob));
      if (!cloudPersistDisabled()) Prefs.set(key, blob);
    } catch {
      /* private mode / quota — the in-memory conversion still applies */
    }
  }, []);

  /** The user's own stored layout, converted to the current shape if it is an
   *  older record (and written back once). A record we cannot read returns
   *  null and is LEFT IN PLACE — never deleted, never overwritten blind. */
  const readOwn = React.useCallback(
    (key: string, raw: unknown): DockBlob | null => {
      const read = readBlob(raw, dockSize());
      if (!read) return null;
      if (read.migrated) writeBack(key, read.blob);
      return read.blob;
    },
    [dockSize, writeBack]
  );

  const handleReady = (event: DockviewReadyEvent) => {
    const api = event.api;
    apiRef.current = api;
    const key = layoutKeyRef.current;

    // The ladder (contract §2.9): the user's own layout, then the FIRM
    // default, then the app's out-of-box arrangement. Local first (instant),
    // then the cloud copy if it's newer — so the machine you tweaked last
    // always wins, everywhere.
    let restored = false;
    const local = readOwn(key, localStorage.getItem(key));
    if (local) restored = applyBlob(local);
    let inherited = false;
    if (!restored) {
      const firm = firmBlob();
      if (firm) inherited = restored = applyInherited(firm);
    }
    if (!restored) buildDefault();
    const hadOwnLayout = !!local;
    // The Document is load-bearing: if a stale layout dropped it, put it back.
    if (!api.getPanel('canvas')) addPalette('canvas');
    enforceAnchors();
    computeGroupSizes();

    const tryCloud = () => {
      if (suspensionRef.current.isSuspended()) return;
      // An older pixel-shaped cloud record converts on the way in (same key,
      // same ts — the newest-wins comparison below is unchanged).
      const cloud = readBlob(Prefs.get(key), dockSize())?.blob;
      if (cloud && cloud.layout && cloud.ts > appliedTsRef.current) {
        if (applyBlob(cloud)) {
          if (!api.getPanel('canvas')) addPalette('canvas');
          enforceAnchors();
          refreshOpen();
          computeGroupSizes();
          try {
            localStorage.setItem(key, JSON.stringify(cloud));
          } catch {
            /* non-fatal */
          }
        } else {
          // fromJSON CLEARS the dock before it throws — a corrupt cloud blob
          // must never leave the user staring at an empty editor. Rebuild.
          const local = readOwn(key, localStorage.getItem(key));
          if (!(local && applyBlob(local))) buildDefault();
          if (!api.getPanel('canvas')) addPalette('canvas');
          enforceAnchors();
          refreshOpen();
          computeGroupSizes();
        }
      }
    };
    tryCloudRef.current = tryCloud;
    if (Prefs.isReady()) tryCloud();
    else {
      const un = Prefs.subscribe(() => {
        un();
        tryCloud();
      });
    }
    // Cross-machine follow: the login-time pref snapshot goes stale, so
    // re-pull from the server on every dock mount (= every editor entry).
    // A layout saved on the other machine applies here without a re-login.
    Prefs.refetch().then(tryCloud);

    // The firm default may still be in flight at mount. If this user has no
    // layout of their own anywhere, take the firm's as soon as it lands —
    // a new hire should never see out-of-box when their firm has published
    // an arrangement.
    const tryFirm = () => {
      if (hadOwnLayout || inherited) return;
      if (appliedTsRef.current > 0) return; // the user's cloud layout arrived first
      const firm = firmBlob();
      if (!firm) return;
      if (applyInherited(firm)) {
        inherited = true;
        if (!api.getPanel('canvas')) addPalette('canvas');
        enforceAnchors();
        refreshOpen();
        computeGroupSizes();
      }
    };
    if (FirmPrefs.isReady()) tryFirm();
    else {
      const unFirm = FirmPrefs.subscribe(() => {
        unFirm();
        tryFirm();
      });
    }

    // Left/right docks are SUPERIOR (DESIGN.md §4): a top/bottom drop on the
    // dock's outer edge would span the full width and squeeze the side docks.
    // Intercept it and land the panel in the CENTER column (relative to the
    // Document) instead — full-height side docks keep their width, top/bottom
    // strips get only the width between them.
    api.onWillDrop((e) => {
      if (e.group) return; // in-column drops are fine anywhere
      if (e.position !== 'top' && e.position !== 'bottom') return;
      const data = e.getData();
      if (!data) return; // external tray drags are re-routed in handleDrop
      e.preventDefault();
      const canvasGroup = api.getPanel('canvas')?.group;
      if (!canvasGroup) return;
      const target = data.panelId ? api.getPanel(data.panelId)?.api : undefined;
      const groupTarget = !data.panelId
        ? api.groups.find((g) => g.id === data.groupId)?.api
        : undefined;
      (target ?? groupTarget)?.moveTo({ group: canvasGroup, position: e.position });
    });

    api.onDidLayoutChange(() => {
      persistSoon();
      protectCanvasSoon();
      refreshOpen();
      enforceAnchors();
      computeGroupSizes();
    });
    api.onDidAddPanel(() => {
      refreshOpen();
      computeGroupSizes();
      protectCanvasSoon();
    });
    api.onDidRemovePanel(() => {
      refreshOpen();
      computeGroupSizes();
      // A close/undock frees width dockview otherwise hands to whichever
      // SURVIVING sibling it considers flexible — never reliably the canvas.
      protectCanvasSoon();
    });
    // Accept drags of external palette chips so dockview shows drop overlays —
    // EXCEPT a drop INTO the canvas (the anchored Document). A center-drop there
    // is a no-op, so lighting it up is misleading; leave it unhandled (no
    // overlay). Docking a palette to the canvas's edges still works.
    api.onUnhandledDragOverEvent((e) => {
      // A module dragged out of the Modules panel is headed for the DOCUMENT
      // (useModuleInsert owns that drop) — it is never a panel to dock, so
      // no dock overlay may light up under it or swallow its drop.
      const types = Array.from(e.nativeEvent?.dataTransfer?.types || []);
      if (types.includes(MODULE_DRAG_MIME)) return;
      const overCanvas = (e as any).group?.panels?.some((p: any) => p.id === 'canvas');
      if (overCanvas && (e as any).position === 'center') return;
      e.accept();
    });
    refreshOpen();
    computeGroupSizes();

    const handle: FormaDockHandle = {
      // Open it if it isn't placed, and ALWAYS bring it forward: asking for a
      // tool that is buried behind three tabs must surface it, not no-op.
      showPanel: (id) => {
        addPalette(id);
        try {
          api.getPanel(id)?.api.setActive();
        } catch {
          /* best-effort */
        }
      },
      hidePanel: (id) => api.getPanel(id)?.api.close(),
      togglePanel: (id) => (api.getPanel(id) ? api.getPanel(id)?.api.close() : addPalette(id)),
      isPanelOpen: (id) => !!api.getPanel(id),
      resetLayout: () => {
        try {
          localStorage.removeItem(layoutKeyRef.current);
        } catch {
          /* ignore */
        }
        // Reset means "put me back where a colleague with no preferences
        // would be" — which is the FIRM default when one exists, and only
        // then the out-of-box arrangement (contract §2.9).
        const firm = firmBlob();
        if (!(firm && applyInherited(firm))) buildDefault();
        if (!api.getPanel('canvas')) addPalette('canvas');
        enforceAnchors();
        refreshOpen();
        computeGroupSizes();
        // buildDefault fires onDidLayoutChange → persist, which re-saves the
        // fresh default to BOTH stores (so reset propagates across machines).
      },
      snapshotLayout: () => snapshot(),
      suspend: () => {
        suspensionRef.current.suspend(
          // The LIVE grid, not the stored blob — it is what comes back.
          () => api.toJSON(),
          () => {
            if (persistSoonRef.current) clearTimeout(persistSoonRef.current);
            for (const p of [...api.panels]) {
              if (p.id === 'canvas') continue;
              try {
                p.api.close();
              } catch {
                /* best-effort */
              }
            }
            refreshOpen();
            computeGroupSizes();
          }
        );
      },
      resume: () => {
        suspensionRef.current.resume((snap) => {
          // The restore's own layout events must not save (byte-identical
          // stored layout), and the clamp baseline restarts so the width
          // change from the review column leaving never re-sizes a rail.
          inheritUntilRef.current = Date.now() + 1500;
          retryAfterWindowRef.current = true;
          clampBaseRef.current = null;
          try {
            api.fromJSON(snap as Parameters<typeof api.fromJSON>[0]);
          } catch (e) {
            console.warn('[dock] resume failed — restoring saved layout:', e);
            const own = readOwn(layoutKeyRef.current, localStorage.getItem(layoutKeyRef.current));
            if (!(own && applyBlob(own))) buildDefault();
          }
          if (!api.getPanel('canvas')) addPalette('canvas');
          enforceAnchors();
          refreshOpen();
          computeGroupSizes();
        });
      },
    };
    onReady?.(handle);
  };

  const handleDrop = (event: DockviewDidDropEvent) => {
    // Internal tab moves carry dockview data — let the engine handle those.
    if (event.getData()) return;
    const id = event.nativeEvent.dataTransfer?.getData('application/x-forma-palette');
    const api = apiRef.current;
    if (!id || !api || !defOf(id) || api.getPanel(id)) return;
    const def = defOf(id)!;
    const dir = POSITION_TO_DIRECTION[event.position as Position] ?? 'right';
    const group = (event as any).group;
    // Left/right docks are SUPERIOR: an outer-edge top/bottom drop lands in
    // the center column (relative to the Document), never spanning full width.
    const position = group
      ? { referenceGroup: group.id, direction: dir }
      : dir === 'above' || dir === 'below'
        ? { referencePanel: 'canvas', direction: dir }
        : { direction: dir };
    api.addPanel({
      id,
      component: 'panel',
      tabComponent: 'forma',
      title: def.title,
      position: position as any,
      minimumWidth: scalePx(def.minWidth ?? DEFAULT_MIN_PANEL_WIDTH),
      minimumHeight: scalePx(def.minHeight ?? DEFAULT_MIN_PANEL_HEIGHT),
    });
    // Give the newly-docked palette a SENSIBLE default size — dockview otherwise
    // splits the space 50/50 (a palette eating half the screen). Side docks use
    // the palette's natural width; top/bottom docks get a strip tall enough for
    // the horizontal one-row flow. Both follow the dial, then the fraction cap.
    try {
      const panel = api.getPanel(id);
      if (dir === 'left' || dir === 'right')
        panel?.api.setSize({ width: scalePx(def.width ?? 320) });
      else if (dir === 'above' || dir === 'below')
        panel?.api.setSize({ height: scalePx(STRIP_HEIGHT) });
    } catch {
      /* best-effort */
    }
  };

  const onDockGestureEnd = () => {
    persistSoon();
    protectCanvasSoon();
  };

  return (
    <DockContext.Provider value={ctxValue}>
      <div className="forma-dock-wrap">
        <div
          className="forma-dock-body"
          ref={bodyRef}
          // Sash drags end in a mouseup/dragend that dockview doesn't surface
          // as a layout event — capture them here so resizes actually save,
          // and re-check the canvas floor the same way.
          onMouseUpCapture={onDockGestureEnd}
          onDragEndCapture={onDockGestureEnd}
        >
          <DockviewReact
            // Remount when the layout key changes (anon → user id at sign-in):
            // the restore path re-runs against the RIGHT key instead of saving
            // one user's workspace under another key.
            key={layoutKey}
            className={`forma-dock ${isDark ? 'dockview-theme-dark' : 'dockview-theme-light'}${locked ? ' locked' : ''}`}
            onReady={handleReady}
            onDidDrop={handleDrop}
            components={COMPONENTS}
            tabComponents={TAB_COMPONENTS}
            defaultTabComponent={FormaTab}
            locked={locked}
            disableDnd={locked}
            disableFloatingGroups={false}
          />
        </div>
      </div>
    </DockContext.Provider>
  );
}
